/**
 * AppUserPageV2 — redesigned App Users Lookup page.
 *
 * This is the most complex viewer in the CMS: search → user details
 * card with action buttons → Stripe info, RevenueCat subscriptions
 * (multi-store tabs), RevenueCat profile modal, Grant Premium modal,
 * audit log accordion.
 *
 * V2 scope here (same tradeoff struck for ClickFunnels' SetupGuide):
 *
 *   • Chrome — PageHeader, search bar, user summary card, action
 *     buttons — fully V2 (Button + IconButton + StatusPill + the
 *     redesign palette).
 *   • Modals — RevenueCat profile + Grant Premium ported to v2
 *     Modal (eyebrow / title / footer recipe).
 *   • Logs — RailCard collapsible.
 *
 *   • The Stripe data block, the multi-store RC subscriptions table,
 *     and the deeply-nested entitlement detail inside the RC modal
 *     keep their existing MUI internals — they're tightly coupled
 *     to specific data layouts (Stripe customer payload, RC active
 *     entitlements, etc.) and a clean redesign is its own task.
 *     Each is wrapped in a v2 surface card so it doesn't sit on
 *     bare bg-bg.
 *
 * Same business logic as v1: findByIdOrEmail / deleteUserByEmail /
 * sendPasswordResetLink / processStripeSubscription / grantPremiumAccess
 * / extendInitiative.
 */
import React, { useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { extendInitiative } from '../../../../services/marketingInitiatives';
import {
  REVENUECAT_PROFILE_URL_PREFIX,
  REVENUECAT_PROJECT_ID_PREFIX,
} from '../../../../constants/revenueCat';
import RevenueCatIcon from '../../../Elements/RevenueCatIcon';
import { FaStripeS } from 'react-icons/fa';
import {
  deleteUserByEmail,
  findByIdOrEmail,
  sendPasswordResetLink,
  processStripeSubscription,
  grantPremiumAccess,
} from '../../../../services/userServices';
import LoadingBackdrop from '../../../Common/LoadingBackdrop';
import {
  PageHeader,
  Button,
  StatusPill,
  Modal,
  LibTable,
  Tooltip,
} from '../../../redesign';
import {
  FieldBlock,
  Textarea,
  Select,
  RailCard,
  RailCardHead,
  ToggleSwitch,
} from '../../../redesign/edit';

/* ─── Icons ──────────────────────────────────────────────────── */

const SearchIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const RefreshIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
const EditIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);
const ClearIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const TrashIcon = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);
const MailIcon = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

/* ─── Helpers (verbatim from v1) ─────────────────────────────── */

function formatPrice(price, currency) {
  if (price == null) return null;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(price);
  } catch {
    return `${price} ${currency || ''}`.trim();
  }
}
function getPeriodTypeLabel(periodType) {
  switch (periodType) {
    case 'trial': return 'Free Trial';
    case 'intro': return 'Intro Offer';
    default: return null;
  }
}
function formatRevenueCatTimestamp(timestamp) {
  if (!timestamp) return 'N/A';
  return new Date(timestamp).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: 'numeric', hour12: true,
    timeZone: 'UTC', timeZoneName: 'shortOffset',
  }).replace('GMT', 'UTC');
}
function calculateDaysRemaining(expirationTimestamp) {
  if (!expirationTimestamp) return null;
  const now = new Date();
  const expiration = new Date(expirationTimestamp);
  const diffTime = expiration - now;
  return {
    days: Math.floor(diffTime / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60)),
    totalDays: diffTime / (1000 * 60 * 60 * 24),
  };
}
function formatTimeRemaining(t) {
  if (!t) return '';
  const parts = [];
  if (t.days > 0)    parts.push(`${t.days} day${t.days !== 1 ? 's' : ''}`);
  if (t.hours > 0)   parts.push(`${t.hours} hour${t.hours !== 1 ? 's' : ''}`);
  if (t.minutes > 0) parts.push(`${t.minutes} minute${t.minutes !== 1 ? 's' : ''}`);
  return parts.join(', ');
}
function getStoreLabel(store) {
  return {
    rc_billing: 'RevenueCat Billing (Web)',
    app_store:  'App Store',
    play_store: 'Play Store',
    stripe:     'Stripe',
    amazon:     'Amazon',
    promotional:'Promotional',
  }[store] || store;
}
function formatAttributeKey(key) {
  let k = key.startsWith('$') ? key.substring(1) : key;
  k = k.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1');
  return k.split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
}

function getAutoRenewalLabel(autoRenewalStatus) {
  if (autoRenewalStatus === 'will_renew')     return { label: 'Will Renew',     color: 'success' };
  if (autoRenewalStatus === 'will_not_renew') return { label: 'Will Not Renew', color: 'warning' };
  return { label: autoRenewalStatus || 'Unknown', color: 'default' };
}

/* RevenueCat status / auto-renewal / time-remaining → StatusPill tone.
 * (Replaces the old MUI-palette color maps now that the RC cards are
 * native StatusPills instead of <Chip>s.) */
function rcStatusTone(status) {
  return {
    active:               'green',
    expired:              'coral',
    trialing:             'blue',
    grace_period:         'amber',
    billing_retry_period: 'amber',
  }[status] || 'gray';
}
function rcAutoRenewalTone(color) {
  return { success: 'green', warning: 'amber' }[color] || 'gray';
}
/* Urgency of the remaining window: <=7d coral, <=30d amber, else green.
 * Rendered as a StatusPill (self-contained light tint + dark text) so it
 * stays legible on any card background in both light and dark modes —
 * unlike a colored text token, which has no flipping "amber". */
function remainingTone(totalDays) {
  return totalDays <= 7 ? 'coral' : totalDays <= 30 ? 'amber' : 'green';
}

function renderLogMetadata(metadata) {
  if (!metadata) return <span style={{ color: '#9BA4AD' }}>N/A</span>;
  if (typeof metadata === 'object' && Object.keys(metadata).length > 0) {
    return (
      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {Object.entries(metadata).map(([k, v]) => (
          <li key={k} style={{ fontSize: 11.5, lineHeight: 1.4, wordBreak: 'break-word' }}>
            <strong className="text-navy">{k}:</strong>{' '}
            <span className="text-text-muted">{String(v)}</span>
          </li>
        ))}
      </ul>
    );
  }
  return <span style={{ fontSize: 12 }}>{String(metadata)}</span>;
}

const DURATION_OPTIONS = ['1wk', '2wk', '1mo', '3mo', '6mo', '1yr', 'lifetime'];

/* ─── Small v2 info pair ─────────────────────────────────────── */

function InfoPair({ label, children }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div
        className="font-condensed font-bold uppercase text-text-muted"
        style={{ fontSize: 10, letterSpacing: '0.08em', marginBottom: 4 }}
      >
        {label}
      </div>
      <div className="text-navy" style={{ fontSize: 13.5, lineHeight: 1.35, wordBreak: 'break-word' }}>
        {children}
      </div>
    </div>
  );
}

/* ─── Main ───────────────────────────────────────────────────── */

export default function AppUserPageV2() {
  const [searchTerm, setSearchTerm] = useState('');
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const searchInputRef = useRef(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [openRevenueCatModal, setOpenRevenueCatModal] = useState(false);
  const [openGrantModal, setOpenGrantModal] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState('1mo');
  const [grantReason, setGrantReason] = useState('');
  const [rcStoreTab, setRcStoreTab] = useState(0);
  const [showExpiredSubs, setShowExpiredSubs] = useState(false);
  const [showSandboxSubs, setShowSandboxSubs] = useState(false);

  const isPremium = useMemo(
    () =>
      !!(
        userData?.revenuecat_profile?.active_entitlements?.items?.length
      ),
    [userData],
  );

  const handleSearch = async () => {
    if (!searchTerm) return;
    setLoading(true);
    const response = await findByIdOrEmail(searchTerm);
    setUserData(response);
    setLoading(false);
    if (!response) {
      setUserData(null);
      setSearchTerm('');
      searchInputRef.current?.focus();
    }
  };
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };
  const handleRefresh = async () => {
    if (!searchTerm) return;
    setLoading(true);
    const response = await findByIdOrEmail(searchTerm);
    if (response) setUserData(response);
    else Swal.fire('Refresh failed', 'Could not re-fetch user data. The displayed data may be stale.', 'warning');
    setLoading(false);
  };
  const handleClearSearch = () => {
    setUserData(null);
    setSearchTerm('');
    setRcStoreTab(0);
    setShowExpiredSubs(false);
    setShowSandboxSubs(false);
    setTimeout(() => searchInputRef.current?.focus(), 500);
  };

  const handleDeleteUser = () => {
    if (!userData?.email) return;
    Swal.fire({
      icon: 'warning',
      text: 'Are you sure you want to delete this User?',
      showConfirmButton: true,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: 'Delete',
    }).then(async (result) => {
      if (result.value) {
        const deleted = await deleteUserByEmail(userData.email, setActionLoading);
        if (deleted) {
          setUserData(null);
          setSearchTerm('');
        }
      }
    });
  };

  const handlePasswordReset = () => {
    if (!userData?.email) return;
    Swal.fire({
      icon: 'warning',
      text: 'Are you sure you want to generate a password reset link for this User?',
      showConfirmButton: true,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: 'Generate',
    }).then((result) => {
      if (result.value) {
        sendPasswordResetLink(userData.email, setActionLoading);
      }
    });
  };

  const handleUnlockStripe = () => {
    if (!userData?.email) return;
    Swal.fire({
      title: 'Redeem Stripe Subscription',
      input: 'text',
      inputLabel: 'Enter Stripe Subscription/Charge ID',
      inputPlaceholder: 'e.g., sub_123 or ch_123',
      showCancelButton: true,
      confirmButtonText: 'Redeem',
      reverseButtons: true,
      inputValidator: (value) => {
        if (!value) return 'You need to enter a Stripe ID!';
        if (!value.startsWith('sub_') && !value.startsWith('ch_'))
          return 'Please enter a valid Stripe subscription or charge ID';
        return null;
      },
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        processStripeSubscription(result.value, setActionLoading)
          .then(() => handleSearch())
          .catch(() => {});
      }
    });
  };

  const handleGrantPremium = async () => {
    if (!userData) return;
    setActionLoading(true);
    try {
      const result = await grantPremiumAccess(userData.id, selectedDuration, grantReason);
      if (result) {
        handleSearch();
        setOpenGrantModal(false);
        setGrantReason('');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtendInitiative = (initiative) => {
    const currentEndDate = new Date(initiative.end_date);
    const minDate = new Date(currentEndDate);
    minDate.setDate(minDate.getDate() + 1);

    Swal.fire({
      title: 'Extend Initiative Duration',
      html: `
        <div style="text-align: left; margin-bottom: 1rem;">
          <p><strong>Current End Date:</strong> ${currentEndDate.toLocaleDateString()}</p>
        </div>
        <input type="date" id="extensionDate" class="swal2-input" min="${minDate.toISOString().split('T')[0]}">
      `,
      showCancelButton: true,
      confirmButtonText: 'Extend',
      reverseButtons: true,
      didOpen: () => {
        const dateInput = document.getElementById('extensionDate');
        dateInput.value = minDate.toISOString().split('T')[0];
      },
      preConfirm: () => {
        const dateInput = document.getElementById('extensionDate');
        const selectedDate = new Date(dateInput.value);
        if (!dateInput.value) {
          Swal.showValidationMessage('Please select a date');
          return false;
        }
        if (selectedDate <= currentEndDate) {
          Swal.showValidationMessage('New end date must be after current end date');
          return false;
        }
        return Math.ceil((selectedDate - currentEndDate) / (1000 * 60 * 60 * 24));
      },
    }).then(async (result) => {
      if (result.isConfirmed && result.value) {
        setActionLoading(true);
        await extendInitiative(initiative, userData.id, result.value, (updated) => {
          setUserData((prev) => ({
            ...prev,
            initiatives: (prev?.initiatives || []).map((i) =>
              i.code === updated.code ? { ...i, ...updated } : i,
            ),
          }));
        });
        setActionLoading(false);
      }
    });
  };

  /* ─── RC subscriptions tab data ──────────────────────────── *
   * Source: userData.revenuecat_subscriptions.items[] — each sub
   * carries a `store` field. Group by store for the tabs, then
   * filter the current store's subs by the Show Expired / Show
   * Sandbox toggles. */
  const rcAllSubs = userData?.revenuecat_subscriptions?.items || [];
  const rcStoreGroups = useMemo(() => {
    return rcAllSubs.reduce((acc, sub) => {
      const store = sub.store || 'unknown';
      if (!acc[store]) acc[store] = [];
      acc[store].push(sub);
      return acc;
    }, {});
  }, [rcAllSubs]);
  const storeKeys = Object.keys(rcStoreGroups);
  const safeRcTab = rcStoreTab < storeKeys.length ? rcStoreTab : 0;
  const currentStoreKey = storeKeys[safeRcTab];
  const currentSubs = currentStoreKey ? rcStoreGroups[currentStoreKey] : [];
  const filteredSubs = currentSubs
    .filter((s) => showExpiredSubs || s.status !== 'expired')
    .filter((s) => showSandboxSubs || s.environment !== 'sandbox');

  return (
    <div className="bg-bg min-h-full">
      <LoadingBackdrop open={loading || actionLoading} />
      <PageHeader
        eyebrow="Users"
        title="App Users Lookup"
      />

      <div style={{ padding: '0 28px 60px' }}>
        {/* Search bar */}
        <div
          className="bg-surface border border-sand"
          style={{
            borderRadius: 12,
            padding: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            marginBottom: 16,
          }}
        >
          <div
            className="flex items-center bg-cream border border-sand"
            style={{ borderRadius: 8, padding: '0 12px', gap: 8, flex: 1, minWidth: 280 }}
          >
            <span className="text-text-muted shrink-0">{SearchIcon}</span>
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyUp={handleKeyPress}
              placeholder="Search by user ID or email…"
              disabled={loading || !!userData}
              autoFocus
              style={{
                flex: 1, minWidth: 0, border: 0, background: 'transparent',
                outline: 'none', fontFamily: 'inherit', fontSize: 13.5,
                // Theme-aware: --color-navy is #2C3E50 in light and
                // flips to #E5E9F0 in dark. The previous hardcoded
                // #2C3E50 left typed text dark-navy on the dark field,
                // nearly invisible in dark mode.
                color: 'rgb(var(--color-navy))', padding: '9px 0',
              }}
            />
          </div>
          <Button
            variant="coral"
            leadingIcon={SearchIcon}
            onClick={handleSearch}
            disabled={loading || !searchTerm || !!userData}
          >
            {loading ? 'Searching…' : 'Search'}
          </Button>
          {userData && (
            <>
              <Button variant="ghost" leadingIcon={RefreshIcon} onClick={handleRefresh} disabled={loading}>
                Refresh
              </Button>
              <Button variant="ghost" leadingIcon={EditIcon} onClick={() => setUserData(null)} disabled={loading}>
                Edit
              </Button>
              <Button variant="ghost" leadingIcon={ClearIcon} onClick={handleClearSearch} disabled={loading}>
                Clear
              </Button>
            </>
          )}
        </div>

        {loading && (
          <div
            className="bg-surface border border-sand text-text-muted italic text-center"
            style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
          >
            Loading user data…
          </div>
        )}

        {userData && !loading && (
          <>
            {/* ─── User Details ─────────────────────────────── */}
            <section
              className="bg-surface border border-sand"
              style={{ borderRadius: 12, padding: 18, marginBottom: 16 }}
            >
              <div
                className="font-condensed font-bold uppercase text-text-muted"
                style={{ fontSize: 11, letterSpacing: '0.08em', marginBottom: 14 }}
              >
                User Details
              </div>

              <div
                className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_240px]"
                style={{ gap: 24 }}
              >
                <div
                  className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
                  style={{ gap: 18, alignContent: 'start' }}
                >
                  <InfoPair label="User ID">
                    <span className="font-mono" style={{ fontSize: 12.5 }}>{userData.id}</span>
                  </InfoPair>
                  <InfoPair label="Full name">
                    {userData.full_name || '—'}
                  </InfoPair>
                  <InfoPair label="Email">
                    <span className="font-mono" style={{ fontSize: 12 }}>{userData.email}</span>
                  </InfoPair>
                  <InfoPair label="Last platform">
                    {userData.os_type || '—'}
                  </InfoPair>
                  <InfoPair label="Premium status">
                    <StatusPill tone={isPremium ? 'green' : 'gray'}>
                      {isPremium ? 'Premium' : 'Not premium'}
                    </StatusPill>
                  </InfoPair>
                </div>

                {/* Action buttons column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'stretch' }}>
                  <Button
                    variant="danger"
                    leadingIcon={TrashIcon}
                    onClick={handleDeleteUser}
                    disabled={actionLoading}
                  >
                    Delete user
                  </Button>
                  <Button
                    variant="ghost"
                    leadingIcon={<RevenueCatIcon width={14} height={14} />}
                    onClick={() => setOpenRevenueCatModal(true)}
                    disabled={
                      !userData.revenuecat_profile ||
                      Object.keys(userData.revenuecat_profile).length === 0
                    }
                  >
                    RevenueCat profile
                  </Button>
                  <Button
                    variant="ghost"
                    leadingIcon={MailIcon}
                    onClick={handlePasswordReset}
                    disabled={actionLoading}
                  >
                    Password reset link
                  </Button>
                  <Button
                    variant="ghost"
                    leadingIcon={
                      <FaStripeS style={{ color: '#635BFF', fontSize: 14 }} />
                    }
                    onClick={handleUnlockStripe}
                    disabled={actionLoading}
                  >
                    Unlock Stripe
                  </Button>
                  {userData.stripe_info?.live_data?.customer?.id && (
                    <Button
                      variant="ghost"
                      leadingIcon={
                        <FaStripeS style={{ color: '#635BFF', fontSize: 14 }} />
                      }
                      onClick={() =>
                        window.open(
                          `https://dashboard.stripe.com/customers/${userData.stripe_info.live_data.customer.id}`,
                          '_blank',
                        )
                      }
                    >
                      View in Stripe ↗
                    </Button>
                  )}
                  {userData.stripe_info?.live_data?.customer?.id && (
                    <p
                      className="text-text-muted"
                      style={{ fontSize: 12, fontStyle: 'italic', maxWidth: 400, marginTop: 4 }}
                    >
                      Heads up: in Stripe, a <code>user_id</code> in the customer metadata is our ClickFunnels account id (a constant — currently <code>305881</code>) — <strong>not</strong> an app user. Use <code>app_user_id</code> for the real buyer.
                    </p>
                  )}
                  {!isPremium && (
                    <Button
                      variant="coral"
                      onClick={() => setOpenGrantModal(true)}
                      disabled={actionLoading}
                    >
                      Grant premium
                    </Button>
                  )}
                </div>
              </div>

            </section>

            {/* ─── Stripe section — kept v1 internals in a v2 card ─── */}
            {userData.stripe_info && (
              <V2CardWrap
                title="Stripe"
                icon={
                  // Match RevenueCatIcon's 20×20 brand-coloured chip
                  // recipe: white logo on the brand-purple square.
                  <span
                    aria-hidden
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 4,
                      background: '#635BFF',
                      display: 'inline-grid',
                      placeItems: 'center',
                      color: '#fff',
                      flexShrink: 0,
                    }}
                  >
                    <FaStripeS style={{ fontSize: 12 }} />
                  </span>
                }
              >
                <StripeSection stripeInfo={userData.stripe_info} />
              </V2CardWrap>
            )}

            {/* ─── RevenueCat subscriptions — multi-store tabs ─── */}
            {storeKeys.length > 0 && (
              <section
                className="bg-surface border border-sand"
                style={{ borderRadius: 12, padding: 0, marginBottom: 16, overflow: 'hidden' }}
              >
                {/* Header strip — title + Show Expired / Show Sandbox
                 *  toggles + View in RevenueCat external link. */}
                <div
                  className="flex items-center justify-between"
                  style={{
                    padding: '14px 18px',
                    borderBottom: '1px solid rgb(var(--color-sand))',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <div className="flex items-center" style={{ gap: 10 }}>
                    <RevenueCatIcon />
                    <div
                      className="font-condensed font-bold uppercase text-text-muted"
                      style={{ fontSize: 11, letterSpacing: '0.08em' }}
                    >
                      RevenueCat Subscriptions
                    </div>
                  </div>
                  <div className="flex items-center" style={{ gap: 16 }}>
                    <ToggleSwitch
                      checked={showExpiredSubs}
                      onChange={(e) => setShowExpiredSubs(e.target.checked)}
                      label="Show Expired"
                    />
                    <ToggleSwitch
                      checked={showSandboxSubs}
                      onChange={(e) => setShowSandboxSubs(e.target.checked)}
                      label="Show Sandbox"
                    />
                    {userData.revenuecat_profile?.id &&
                      userData.revenuecat_profile?.project_id && (
                        <Button
                          variant="ghost"
                          onClick={() =>
                            window.open(
                              `${REVENUECAT_PROFILE_URL_PREFIX}${userData.revenuecat_profile.project_id.replace(REVENUECAT_PROJECT_ID_PREFIX, '')}/${userData.revenuecat_profile.id}`,
                              '_blank',
                            )
                          }
                        >
                          View in RevenueCat ↗
                        </Button>
                      )}
                  </div>
                </div>

                {/* Store tabs */}
                <div
                  className="flex items-center"
                  style={{
                    borderBottom: '1px solid rgb(var(--color-sand))',
                    padding: '0 12px',
                    // Tabs sit flush (like the MUI Tabs this replaced);
                    // each tab's own 10px horizontal padding separates them.
                    gap: 0,
                    overflowX: 'auto',
                  }}
                >
                  {storeKeys.map((key, i) => {
                    const visibleCount = rcStoreGroups[key]
                      .filter((s) => showExpiredSubs || s.status !== 'expired')
                      .filter((s) => showSandboxSubs || s.environment !== 'sandbox')
                      .length;
                    const active = i === safeRcTab;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setRcStoreTab(i)}
                        className="font-condensed font-bold uppercase"
                        style={{
                          border: 0,
                          background: 'transparent',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          fontSize: 11.5,
                          letterSpacing: '0.04em',
                          padding: '12px 10px',
                          whiteSpace: 'nowrap',
                          color: active
                            ? 'rgb(var(--color-navy))'
                            : 'rgb(var(--color-text-muted))',
                          // Underline via inset shadow rather than a
                          // bottom-border + negative margin: the
                          // container's overflowX:auto forces overflowY
                          // to auto, which would clip a -1px overlap. An
                          // inset shadow draws inside the box, so it
                          // can't be clipped and cleanly sits over the
                          // container's 1px bottom border.
                          boxShadow: active
                            ? 'inset 0 -2px 0 rgb(var(--color-teal-green))'
                            : 'none',
                        }}
                      >
                        {`${getStoreLabel(key)} (${visibleCount}/${rcStoreGroups[key].length})`}
                      </button>
                    );
                  })}
                </div>

                {/* Subscription list for the active store */}
                <div style={{ padding: '14px 18px' }}>
                  {currentStoreKey === 'stripe' && (
                    <div
                      className="text-text-muted"
                      style={{
                        fontSize: 12,
                        lineHeight: 1.45,
                        padding: '8px 10px',
                        marginBottom: 12,
                        background: 'rgb(var(--color-cream))',
                        border: '1px solid rgb(var(--color-sand))',
                        borderRadius: 6,
                      }}
                    >
                      These come from external Stripe checkout pages (e.g. ClickFunnels / flash sale) and should match the{' '}
                      <span className="font-bold">Stripe</span> section above.
                    </div>
                  )}
                  {filteredSubs.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {filteredSubs.map((sub, index) => (
                        <RcSubscriptionCard key={index} sub={sub} />
                      ))}
                    </div>
                  ) : (
                    <div
                      className="text-text-muted italic text-center"
                      style={{ padding: '20px 4px', fontSize: 13 }}
                    >
                      No {showExpiredSubs ? '' : 'active '}
                      {showSandboxSubs ? '' : 'production '}
                      subscriptions for {getStoreLabel(currentStoreKey)}.
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* ─── Marketing Initiatives table ─── */}
            <V2CardWrap title={`Marketing initiatives · ${userData.initiatives?.length || 0}`}>
              {Array.isArray(userData.initiatives) && userData.initiatives.length > 0 ? (
                <LibTable
                  fixed
                  className="border border-sand"
                  style={{ borderRadius: 8, overflow: 'hidden' }}
                >
                  <colgroup>
                    {/* name */}
                    <col />
                    {/* code */}
                    <col style={{ width: 130 }} />
                    {/* status */}
                    <col style={{ width: 110 }} />
                    {/* start */}
                    <col style={{ width: 130 }} />
                    {/* end */}
                    <col style={{ width: 130 }} />
                    {/* channel */}
                    <col style={{ width: 120 }} />
                    {/* actions */}
                    <col style={{ width: 150 }} />
                  </colgroup>
                  <LibTable.Head>
                    <tr>
                      <LibTable.Th>Name</LibTable.Th>
                      <LibTable.Th>Code</LibTable.Th>
                      <LibTable.CenterTh>Status</LibTable.CenterTh>
                      <LibTable.Th>Start</LibTable.Th>
                      <LibTable.Th>End</LibTable.Th>
                      <LibTable.Th>Channel</LibTable.Th>
                      <LibTable.Th style={{ textAlign: 'right' }}>Actions</LibTable.Th>
                    </tr>
                  </LibTable.Head>
                  <tbody>
                    {userData.initiatives.map((it) => {
                      const tone = it.status === 'active' ? 'green' : it.status === 'expired' ? 'coral' : 'gray';
                      const canExtend = it.is_active !== 0 && it.deleted_at === null;
                      return (
                        <LibTable.Row key={`init-${it.id}`}>
                          <LibTable.Td>
                            <span className="text-navy" style={{ fontSize: 13, fontWeight: 600 }}>
                              {it.name}
                            </span>
                          </LibTable.Td>
                          <LibTable.Td>
                            <span className="font-mono text-text-muted" style={{ fontSize: 11.5 }}>
                              {it.code}
                            </span>
                          </LibTable.Td>
                          <LibTable.CenterTd>
                            <StatusPill tone={tone}>{it.status}</StatusPill>
                          </LibTable.CenterTd>
                          <LibTable.Td>
                            <span className="font-mono text-text-muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                              {it.start_date ? new Date(it.start_date).toLocaleDateString() : 'N/A'}
                            </span>
                          </LibTable.Td>
                          <LibTable.Td>
                            <span className="font-mono text-text-muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                              {it.end_date ? new Date(it.end_date).toLocaleDateString() : 'N/A'}
                            </span>
                          </LibTable.Td>
                          <LibTable.Td>
                            <span className="text-text-muted" style={{ fontSize: 12 }}>
                              {it.subscription_channel || 'N/A'}
                            </span>
                          </LibTable.Td>
                          <LibTable.Td style={{ textAlign: 'right' }}>
                            {canExtend && (
                              <Button
                                variant="ghost"
                                onClick={() => handleExtendInitiative(it)}
                                disabled={actionLoading}
                              >
                                Extend
                              </Button>
                            )}
                          </LibTable.Td>
                        </LibTable.Row>
                      );
                    })}
                  </tbody>
                </LibTable>
              ) : (
                <div
                  className="bg-cream border border-sand text-text-muted italic text-center"
                  style={{ borderRadius: 8, padding: '20px 14px', fontSize: 13 }}
                >
                  No initiatives found for this user.
                </div>
              )}
            </V2CardWrap>

            {/* ─── Email Change History ─── */}
            {(() => {
              const emailChanges = (Array.isArray(userData.logs) ? userData.logs : []).filter(
                (log) => log?.metadata && log.metadata.action === 'email_change'
              );
              return (
                <V2CardWrap title={`Email change history · ${emailChanges.length}`}>
                  {emailChanges.length > 0 ? (
                    <LibTable
                      fixed
                      className="border border-sand"
                      style={{ borderRadius: 8, overflow: 'hidden' }}
                    >
                      <colgroup>
                        {/* when */}
                        <col style={{ width: 180 }} />
                        {/* from */}
                        <col />
                        {/* to */}
                        <col />
                        {/* source */}
                        <col style={{ width: 150 }} />
                      </colgroup>
                      <LibTable.Head>
                        <tr>
                          <LibTable.Th>When</LibTable.Th>
                          <LibTable.Th>From</LibTable.Th>
                          <LibTable.Th>To</LibTable.Th>
                          <LibTable.Th>Source</LibTable.Th>
                        </tr>
                      </LibTable.Head>
                      <tbody>
                        {emailChanges.map((log) => (
                          <LibTable.Row key={`email-change-${log.id}`}>
                            <LibTable.Td>
                              <span className="font-mono text-text-muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                                {log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}
                              </span>
                            </LibTable.Td>
                            <LibTable.Td>
                              <span className="font-mono text-text-muted" style={{ fontSize: 12, wordBreak: 'break-word' }}>
                                {log.metadata.old_email || '—'}
                              </span>
                            </LibTable.Td>
                            <LibTable.Td>
                              <span className="font-mono text-navy" style={{ fontSize: 12, fontWeight: 600, wordBreak: 'break-word' }}>
                                {log.metadata.new_email || '—'}
                              </span>
                            </LibTable.Td>
                            <LibTable.Td>
                              <span className="text-text-muted" style={{ fontSize: 12 }}>
                                {log.request_source || '—'}
                              </span>
                            </LibTable.Td>
                          </LibTable.Row>
                        ))}
                      </tbody>
                    </LibTable>
                  ) : (
                    <div
                      className="bg-cream border border-sand text-text-muted italic text-center"
                      style={{ borderRadius: 8, padding: '20px 14px', fontSize: 13 }}
                    >
                      No email changes recorded for this user.
                    </div>
                  )}
                </V2CardWrap>
              );
            })()}

            {/* ─── User History Logs ─── */}
            {Array.isArray(userData.logs) && userData.logs.length > 0 && (
              <RailCard
                defaultOpen={false}
                head={({ open, toggle }) => (
                  <RailCardHead
                    eyebrow={`User history logs · ${userData.logs.length}`}
                    open={open}
                    onToggle={toggle}
                  />
                )}
              >
                <div style={{ maxHeight: 480, overflow: 'auto' }}>
                  <LibTable fixed>
                    <colgroup>
                      <col style={{ width: 170 }} />
                      <col style={{ width: 120 }} />
                      <col style={{ width: 100 }} />
                      <col style={{ width: 180 }} />
                      <col style={{ width: 140 }} />
                      <col />
                    </colgroup>
                    <LibTable.Head>
                      <tr>
                        <LibTable.Th>Timestamp</LibTable.Th>
                        <LibTable.Th>Source</LibTable.Th>
                        <LibTable.Th>Status</LibTable.Th>
                        <LibTable.Th>Requested by</LibTable.Th>
                        <LibTable.Th>IP address</LibTable.Th>
                        <LibTable.Th>Action (metadata)</LibTable.Th>
                      </tr>
                    </LibTable.Head>
                    <tbody>
                      {userData.logs.map((log) => (
                        <LibTable.Row key={`log-${log.id}`}>
                          <LibTable.Td>
                            <span className="font-mono text-text-muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                              {log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}
                            </span>
                          </LibTable.Td>
                          <LibTable.Td>
                            <span className="text-navy" style={{ fontSize: 12 }}>
                              {log.request_source || '—'}
                            </span>
                          </LibTable.Td>
                          <LibTable.Td>
                            <span className="text-text-muted" style={{ fontSize: 12 }}>
                              {log.request_status || '—'}
                            </span>
                          </LibTable.Td>
                          <LibTable.Td>
                            <span className="font-mono text-text-muted" style={{ fontSize: 11.5, wordBreak: 'break-word' }}>
                              {log.requested_by || '—'}
                            </span>
                          </LibTable.Td>
                          <LibTable.Td>
                            <span className="font-mono text-text-muted" style={{ fontSize: 11.5 }}>
                              {log.ip_address || '—'}
                            </span>
                          </LibTable.Td>
                          <LibTable.Td>
                            {renderLogMetadata(log.metadata)}
                          </LibTable.Td>
                        </LibTable.Row>
                      ))}
                    </tbody>
                  </LibTable>
                </div>
              </RailCard>
            )}
          </>
        )}
      </div>

      {/* ─── RevenueCat profile modal ─── */}
      {userData?.revenuecat_profile && (
        <Modal
          open={openRevenueCatModal}
          onClose={() => setOpenRevenueCatModal(false)}
          eyebrow="RevenueCat"
          title={`Profile for ${userData.email}`}
          maxWidth={720}
          footer={
            <Button variant="coral" onClick={() => setOpenRevenueCatModal(false)}>
              Close
            </Button>
          }
        >
          <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
            <RcProfileBody profile={userData.revenuecat_profile} />
          </div>
        </Modal>
      )}

      {/* ─── Grant Premium modal ─── */}
      <Modal
        open={openGrantModal}
        onClose={() => setOpenGrantModal(false)}
        eyebrow="Premium"
        title="Grant premium access"
        maxWidth={480}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpenGrantModal(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button variant="coral" onClick={handleGrantPremium} disabled={actionLoading || !grantReason.trim()}>
              {actionLoading ? 'Granting…' : 'Grant'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <FieldBlock label="Duration" htmlFor="f-grant-duration" noMargin>
            <Select
              id="f-grant-duration"
              value={selectedDuration}
              onChange={(e) => setSelectedDuration(e.target.value)}
            >
              {DURATION_OPTIONS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </Select>
          </FieldBlock>
          <FieldBlock label="Reason" required hint="Logged with the grant for audit" htmlFor="f-grant-reason" noMargin>
            <Textarea
              id="f-grant-reason"
              value={grantReason}
              onChange={(e) => setGrantReason(e.target.value)}
              rows={3}
              placeholder="Why are you granting premium access?"
            />
          </FieldBlock>
        </div>
      </Modal>
    </div>
  );
}

/* ─── V2 surface wrapper for the kept-v1 detail sections ─── */
function V2CardWrap({ title, icon, children }) {
  return (
    <section
      className="bg-surface border border-sand"
      style={{ borderRadius: 12, padding: 18, marginBottom: 16, overflow: 'hidden' }}
    >
      <div
        className="flex items-center"
        style={{ gap: 10, marginBottom: 12 }}
      >
        {icon}
        <div
          className="font-condensed font-bold uppercase text-text-muted"
          style={{ fontSize: 11, letterSpacing: '0.08em' }}
        >
          {title}
        </div>
      </div>
      {children}
    </section>
  );
}

/* ─── Stripe section — matches the v1 layout 1:1 (database record
 *  + live customer + active subscriptions). Field names match the
 *  real `stripe_info` shape (`database_record`, `live_data.customer`). */
/* Section sub-header used inside the detail cards — condensed,
 * uppercase, muted; matches the "User Details" header treatment. */
function DetailSubHeader({ children }) {
  return (
    <div
      className="font-condensed font-bold uppercase text-text-muted"
      style={{ fontSize: 10.5, letterSpacing: '0.08em', marginBottom: 10 }}
    >
      {children}
    </div>
  );
}

/* Token-themed hairline (replaces MUI <Divider>). */
function Hairline({ tight }) {
  return (
    <div
      style={{
        height: 1,
        background: 'rgb(var(--color-sand))',
        margin: tight ? '8px 0' : '16px 0',
      }}
    />
  );
}

/* Stripe live-subscription status → StatusPill tone. */
function stripeStatusTone(status) {
  return {
    active: 'green',
    trialing: 'blue',
    canceled: 'coral',
  }[status] || 'gray';
}

function StripeSection({ stripeInfo }) {
  const db = stripeInfo.database_record;
  const live = stripeInfo.live_data;
  const typeLabel = db
    ? db.is_lifetime ? 'Lifetime'
      : db.is_yearly ? 'Yearly'
      : db.is_monthly ? 'Monthly'
      : 'N/A'
    : 'N/A';
  const mono = (v) => <span className="font-mono" style={{ fontSize: 12 }}>{v}</span>;
  return (
    <div>
      <DetailSubHeader>Database record</DetailSubHeader>
      {db ? (
        <div className="flex flex-wrap" style={{ gap: 18, marginBottom: 16 }}>
          <InfoPair label="Customer ID">{mono(db.stripe_customer_id || 'N/A')}</InfoPair>
          <InfoPair label="Transaction ID">{mono(db.stripe_transaction_id || 'N/A')}</InfoPair>
          <InfoPair label="Subscription Plan">{db.subscription_plan || 'N/A'}</InfoPair>
          <InfoPair label="Type">{typeLabel}</InfoPair>
          <InfoPair label="Status">
            <StatusPill tone={db.is_trial ? 'blue' : 'green'}>
              {db.is_trial ? 'Trial' : 'Active'}
            </StatusPill>
          </InfoPair>
          <InfoPair label="Purchased">
            {db.purchased_at ? new Date(db.purchased_at * 1000).toLocaleDateString() : 'N/A'}
          </InfoPair>
          <InfoPair label="Expires">
            {db.expiration_date ? new Date(db.expiration_date * 1000).toLocaleDateString() : 'N/A'}
          </InfoPair>
        </div>
      ) : (
        <div className="text-coral-dark" style={{ fontSize: 13, marginBottom: 16 }}>
          No database record found
        </div>
      )}

      <Hairline />

      <DetailSubHeader>Live Stripe data</DetailSubHeader>
      {live?.customer ? (
        <>
          <div className="flex flex-wrap" style={{ gap: 18, marginBottom: 16 }}>
            <InfoPair label="Customer ID">{mono(live.customer.id)}</InfoPair>
            <InfoPair label="Email">{live.customer.email}</InfoPair>
            <InfoPair label="Created">
              {new Date(live.customer.created * 1000).toLocaleDateString()}
            </InfoPair>
          </div>

          <Hairline />

          <DetailSubHeader>Active subscriptions</DetailSubHeader>
          {Array.isArray(live.customer.subscriptions) && live.customer.subscriptions.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {live.customer.subscriptions.map((sub, i) => (
                <div
                  key={i}
                  className="bg-cream border border-sand"
                  style={{ borderRadius: 8, padding: 12 }}
                >
                  <div
                    className="flex items-start justify-between"
                    style={{ gap: 16, flexWrap: 'wrap' }}
                  >
                    <div>
                      <div className="text-navy" style={{ fontSize: 13 }}>
                        <strong>Plan:</strong> {sub.plan?.nickname || '—'}
                      </div>
                      <div style={{ marginTop: 4 }}>
                        <StatusPill tone={stripeStatusTone(sub.status)}>{sub.status}</StatusPill>
                      </div>
                      {sub.plan?.amount != null && sub.plan?.currency && (
                        <div className="text-navy" style={{ fontSize: 13, marginTop: 4 }}>
                          <strong>Amount:</strong>{' '}
                          {(sub.plan.amount / 100).toLocaleString('en-US', {
                            style: 'currency',
                            currency: sub.plan.currency.toUpperCase(),
                          })}{' '}
                          / {sub.plan.interval}
                        </div>
                      )}
                    </div>
                    <div>
                      <div
                        className="font-mono text-navy"
                        style={{ fontSize: 12, wordBreak: 'break-all' }}
                      >
                        <strong>ID:</strong> {sub.id}
                      </div>
                      {sub.cancel_at_period_end && (
                        <div className="text-coral-dark" style={{ fontSize: 11.5, marginTop: 4 }}>
                          Will cancel at period end
                        </div>
                      )}
                    </div>
                  </div>
                  <Hairline tight />
                  <div>
                    <div className="text-navy" style={{ fontSize: 13 }}>
                      <strong>Current period:</strong>
                    </div>
                    <div className="flex flex-wrap" style={{ gap: 16, marginLeft: 16 }}>
                      <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                        Start: {new Date(sub.current_period_start * 1000).toLocaleString()}
                      </span>
                      <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                        End: {new Date(sub.current_period_end * 1000).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-text-muted" style={{ padding: '16px 4px', textAlign: 'center', fontSize: 13 }}>
              No active subscriptions
            </div>
          )}
        </>
      ) : (
        <div className="text-coral-dark" style={{ fontSize: 13 }}>No live Stripe data found</div>
      )}
    </div>
  );
}

/* ─── Single RC subscription card. Matches the v1 layout: header
 *  row with display_name (product_id sub) + status chip +
 *  auto-renewal chip + period-type chip, current-period row with
 *  start/end + "X left" if not expired, original purchase + price
 *  if present, and the Manage Subscription / Docs buttons when
 *  the sub carries a management_url. */
function RcSubscriptionCard({ sub }) {
  const periodEnd = sub.current_period_end || null;
  const timeRemaining = periodEnd ? calculateDaysRemaining(periodEnd) : null;
  const autoRenewal = getAutoRenewalLabel(sub.auto_renewal_status);
  const periodLabel = getPeriodTypeLabel(sub.period_type);

  // Active subs get a soft teal tint (flips light↔dark via the token);
  // expired subs are dimmed; everything else is a plain cream card.
  const active = sub.status === 'active';
  const expired = sub.status === 'expired';

  return (
    <div
      style={{
        borderRadius: 10,
        padding: 12,
        background: active ? 'var(--color-teal-green-soft)' : 'rgb(var(--color-cream))',
        border: active
          ? '1px solid rgb(var(--color-teal-green) / 0.4)'
          : '1px solid rgb(var(--color-sand))',
        opacity: expired ? 0.7 : 1,
      }}
    >
      <div className="flex items-center" style={{ gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span className="text-navy" style={{ fontSize: 13.5 }}>
          <strong>{sub.display_name || sub.product_id}</strong>
          {sub.display_name && (
            <span className="text-text-muted" style={{ marginLeft: 4, fontSize: 11.5 }}>
              ({sub.product_id})
            </span>
          )}
        </span>
        <StatusPill tone={rcStatusTone(sub.status)}>{sub.status}</StatusPill>
        {sub.status !== 'expired' && (
          <StatusPill tone={rcAutoRenewalTone(autoRenewal.color)}>{autoRenewal.label}</StatusPill>
        )}
        {periodLabel && (
          <StatusPill tone={sub.period_type === 'trial' ? 'blue' : 'gray'}>{periodLabel}</StatusPill>
        )}
      </div>

      <Hairline tight />

      <div>
        <div className="text-navy" style={{ fontSize: 13 }}>
          <strong>Current period:</strong>
        </div>
        <div className="flex" style={{ gap: 16, marginLeft: 16, marginTop: 2, flexWrap: 'wrap' }}>
          <span className="text-text-muted" style={{ fontSize: 11.5 }}>
            Start: {sub.current_period_start ? formatRevenueCatTimestamp(sub.current_period_start) : 'N/A'}
          </span>
          <span className="text-text-muted" style={{ fontSize: 11.5 }}>
            End: {periodEnd ? formatRevenueCatTimestamp(periodEnd) : 'N/A'}
          </span>
        </div>
        {timeRemaining && timeRemaining.totalDays > 0 && (
          <div style={{ marginLeft: 16, marginTop: 6 }}>
            <StatusPill tone={remainingTone(timeRemaining.totalDays)}>
              {formatTimeRemaining(timeRemaining)} remaining
            </StatusPill>
          </div>
        )}
      </div>

      {(sub.original_purchase_date || sub.price != null) && (
        <div style={{ marginTop: 8, marginLeft: 16 }}>
          {sub.original_purchase_date && (
            <div className="text-text-muted" style={{ fontSize: 11.5 }}>
              Original purchase: {formatRevenueCatTimestamp(sub.original_purchase_date)}
            </div>
          )}
          {sub.price != null && (
            <div className="text-text-muted" style={{ fontSize: 11.5 }}>
              Price: {formatPrice(sub.price, sub.currency)}
            </div>
          )}
        </div>
      )}

      {sub.management_url && (
        <div className="flex items-center" style={{ marginTop: 8, gap: 8, flexWrap: 'wrap' }}>
          <Tooltip content="Sends an email to the user with a link to log in to RevenueCat's customer portal, where they can manage their subscription (cancel, change plan, update payment method).">
            <Button
              variant="ghost"
              onClick={() => window.open(sub.management_url, '_blank')}
            >
              Manage subscription ↗
            </Button>
          </Tooltip>
          <Tooltip content="RevenueCat docs: Managing customer subscriptions">
            <Button
              variant="ghost"
              onClick={() =>
                window.open(
                  'https://www.revenuecat.com/docs/web/web-billing/managing-customer-subscriptions',
                  '_blank',
                )
              }
            >
              Docs ↗
            </Button>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

/* RC profile section heading. */
function RevHeading({ children }) {
  return (
    <div className="text-navy font-bold" style={{ fontSize: 15, marginBottom: 8 }}>
      {children}
    </div>
  );
}

/* Value-on-top, condensed-uppercase label below — the RC profile's
 * inverse of InfoPair (matches the v1 RevenueCat panel layout). */
function RevField({ children, label, mono }) {
  return (
    <div>
      <div
        className={mono ? 'text-navy font-mono' : 'text-navy'}
        style={{ fontSize: mono ? 12 : 13.5, lineHeight: 1.35, wordBreak: 'break-all' }}
      >
        {children}
      </div>
      <div
        className="font-condensed font-bold uppercase text-text-muted"
        style={{ fontSize: 10, letterSpacing: '0.08em', marginTop: 2 }}
      >
        {label}
      </div>
    </div>
  );
}

/* ─── RC profile body — kept v1 layout (1:1 with original), now
 *  redesign-native: Active entitlements (expires_at + remaining-time
 *  pill), then Customer Details (app user ID, first seen, last seen,
 *  RC profile external link), then Attributes (items[] of {name,
 *  value}). Panels are token-native cream cards (dark-mode correct). */
function RcProfileBody({ profile }) {
  return (
    <div>
      {/* Active entitlements / Premium Access */}
      <RevHeading>Active entitlements / Premium access</RevHeading>
      <div
        className="bg-cream border border-sand"
        style={{ borderRadius: 10, padding: 12, marginBottom: 16 }}
      >
        {profile.active_entitlements?.items?.length > 0 ? (
          profile.active_entitlements.items.map((entitlement, index) => {
            const timeRemaining = calculateDaysRemaining(entitlement.expires_at);
            const isLast = index === profile.active_entitlements.items.length - 1;
            return (
              <React.Fragment key={`entitlement-${index}`}>
                <div style={{ marginBottom: isLast ? 0 : 8 }}>
                  <div className="flex items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
                    <span className="text-navy" style={{ fontSize: 13.5, wordBreak: 'break-all' }}>
                      {formatRevenueCatTimestamp(entitlement.expires_at)}
                    </span>
                    {timeRemaining !== null && (
                      <StatusPill tone={remainingTone(timeRemaining.totalDays)}>
                        {formatTimeRemaining(timeRemaining)} remaining
                      </StatusPill>
                    )}
                  </div>
                  <div
                    className="font-condensed font-bold uppercase text-text-muted"
                    style={{ fontSize: 10, letterSpacing: '0.08em', marginTop: 2 }}
                  >
                    Expires at
                  </div>
                </div>
                {!isLast && <Hairline tight />}
              </React.Fragment>
            );
          })
        ) : (
          <span className="text-navy" style={{ fontSize: 13.5 }}>User is not premium</span>
        )}
      </div>

      {/* Customer Details */}
      <RevHeading>Customer details</RevHeading>
      <div
        className="bg-cream border border-sand"
        style={{ borderRadius: 10, padding: 12, marginBottom: 16 }}
      >
        <RevField label="App user ID" mono>{profile.id}</RevField>
        <Hairline tight />
        <RevField label="First seen">{formatRevenueCatTimestamp(profile.first_seen_at)}</RevField>
        <Hairline tight />
        <RevField label="Last seen">{formatRevenueCatTimestamp(profile.last_seen_at)}</RevField>
        {profile.project_id && (
          <>
            <Hairline tight />
            <RevField label="RevenueCat profile link">
              <a
                href={`${REVENUECAT_PROFILE_URL_PREFIX}${profile.project_id.replace(REVENUECAT_PROJECT_ID_PREFIX, '')}/${profile.id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'rgb(var(--color-teal-green-dark))' }}
              >
                View in RevenueCat ↗
              </a>
            </RevField>
          </>
        )}
      </div>

      {/* Attributes (items[].name / .value) */}
      {profile.attributes?.items?.length > 0 && (
        <>
          <RevHeading>Attributes</RevHeading>
          <div className="bg-cream border border-sand" style={{ borderRadius: 10, padding: 12 }}>
            {profile.attributes.items.map((attribute, index) => {
              const isLast = index === profile.attributes.items.length - 1;
              return (
                <React.Fragment key={`attribute-${index}`}>
                  <RevField label={formatAttributeKey(attribute.name)}>
                    {attribute.value}
                  </RevField>
                  {!isLast && <Hairline tight />}
                </React.Fragment>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
